import {
  Box,
  Typography,
  Stack,
  debounce,
  StackProps,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useMemo, useState } from 'react';
import { ECElementEvent } from 'echarts';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';
import { buildThreeDPieChartOptions } from '../chartWidgetUtility';

export interface ThreeDPieChartData {
  name: string;
  value: number;
  percentage?: number | string;
  color?: string;
}

interface ThreeDPieChartProps {
  isLoading?: boolean;
  title?: string;
  subTitle?: string;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  hasTooltip?: boolean;
  seriesData: ThreeDPieChartData[];
  pieColors?: string[];
  handleEvent?: (
    event: ECElementEvent,
    title?: string,
  ) => void;
  showDataLabels?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  chartHeight?: number | string;
  pieRadius?: string | number;
  depth?: number;
  center?: [string, string];
  containerProps?: StackProps;
  valueType?: 'cost' | 'number';
  decimalPlaces?: number;
  legendPosition?: 'left' | 'center' | 'right';
  labelLineLength?: number;
}

const ThreeDPieChart = ({
  isLoading = false,
  title = '',
  subTitle,
  tooltipLabel = '',
  hasTooltip = false,
  seriesData,
  pieColors = [],
  handleEvent,
  showDataLabels = true,
  showLegend = true,
  showTooltip = true,
  chartHeight = 420,
  pieRadius = '65%',
  depth = 18,
  center = ['50%', '45%'],
  containerProps,
  valueType = 'number',
  decimalPlaces = 2,
  legendPosition = 'center',
  labelLineLength = 22,
}: Readonly<ThreeDPieChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [windowSize, setWindowSize] = useState(
    window.innerWidth,
  );

  const options = useMemo(
    () =>
      buildThreeDPieChartOptions({
        theme,
        seriesData,
        pieColors,
        showDataLabels,
        showLegend,
        showTooltip,
        pieRadius,
        depth,
        center,
        valueType,
        decimalPlaces,
        legendPosition,
        labelLineLength
      }),
    [
      theme,
      seriesData,
      pieColors,
      showDataLabels,
      showLegend,
      showTooltip,
      pieRadius,
      depth,
      center,
      valueType,
      decimalPlaces,
      legendPosition,
      labelLineLength,
    ],
  );

  useEffect(() => {
    const onResize = debounce(() => {
      setWindowSize(window.innerWidth);
    }, 200);

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      onResize.clear();
    };
  }, []);

  const renderChartContent = () => {
    if (isLoading) {
      return <LoaderComponent />;
    }

    if (!options) {
      return (
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            variant="bodyText"
            sx={{ color: colors.widget.text.main }}
          >
            No Data Available
          </Typography>
        </Stack>
      );
    }

    return (
      <ReactECharts
        option={options}
        style={{
          width: '100%',
          height: '100%',
        }}
        opts={{
          renderer: 'canvas',
        }}
        notMerge
        lazyUpdate
        onEvents={{
          click: (params: ECElementEvent) => {
            handleEvent?.(params, title);
          },
        }}
      />
    );
  };

  return (
    <Box
      display="flex"
      flexGrow={1}
      height="100%"
      position="relative"
    >
      <Stack
        minHeight="100px"
        p={2}
        borderRadius="5px"
        flexGrow={1}
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          color: `${colors.widget.text.main} !important`,
        }}
        {...containerProps}
      >
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
          >
            <Typography
              variant="widgetTitle"
              sx={{
                color: `${colors.widget.text.title} !important`,
                mb: 2
              }}
            >
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && Boolean(tooltipLabel) && (
              <InfoTooltip label={tooltipLabel} />
            )}
          </Stack>

          {subTitle && (
            <Typography
              variant="bodyText"
              sx={{
                color: colors.widget.text.main,
              }}
            >
              {subTitle}
            </Typography>
          )}
        </Stack>

        <Stack
          flexGrow={1}
          justifyContent="center"
          key={windowSize}
          minHeight={chartHeight}
          height={chartHeight}
        >
          {renderChartContent()}
        </Stack>
      </Stack>
    </Box>
  );
};

export default ThreeDPieChart;